<template>
  <div class="space-y-6">
    <p class="text-body text-gray-600 dark:text-gray-300">
      Add the services you offer. You can add pricing, descriptions, and photos for each service.
    </p>

    <!-- Services list -->
    <div v-if="wizard.services.value.length > 0" class="space-y-4">
      <div
        v-for="service in wizard.services.value"
        :key="service.id"
        class="card p-4">
        <div class="flex items-start gap-4">
          <!-- Service photos preview -->
          <div class="flex-shrink-0">
            <div
              v-if="service.photo_urls?.length > 0"
              class="h-16 w-16 overflow-hidden rounded-lg">
              <img
                :src="service.photo_urls[0].url"
                :alt="service.name"
                class="h-full w-full object-cover">
            </div>
            <div
              v-else
              class="flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
              <svg class="h-8 w-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
          </div>

          <!-- Service details -->
          <div class="min-w-0 flex-1">
            <div class="flex items-start justify-between">
              <div>
                <h4 class="font-medium text-gray-900 dark:text-gray-100">
                  {{ service.name }}
                </h4>
                <p v-if="service.description" class="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                  {{ service.description }}
                </p>
                <p v-if="service.price_visible && service.price" class="mt-1 text-sm font-medium text-primary-600 dark:text-primary-400">
                  ${{ service.price }} {{ service.price_unit ? `/ ${service.price_unit}` : '' }}
                </p>
              </div>

              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  @click="editService(service)">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  type="button"
                  class="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                  @click="handleDeleteService(service.id)">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
      <p class="mt-4 text-body text-gray-600 dark:text-gray-300">
        No services added yet
      </p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Add your first service to continue
      </p>
    </div>

    <!-- Add service button -->
    <button
      type="button"
      class="btn btn-secondary btn-full"
      @click="openServiceModal()">
      <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
      Add Service
    </button>

    <!-- Service modal -->
    <div
      v-if="showServiceModal"
      class="fixed inset-0 flex items-center justify-center"
      style="z-index: var(--z-modal);">
      <div class="absolute inset-0" style="background-color: rgb(var(--color-gray-900-rgb) / 0.5);" @click="closeServiceModal" />
      <div class="relative w-full max-w-lg rounded-xl p-6" style="background-color: var(--bg-surface); box-shadow: var(--shadow-xl);">
        <h3 class="heading-4 mb-4" style="color: var(--text-primary);">
          {{ editingService ? 'Edit Service' : 'Add Service' }}
        </h3>

        <div class="space-y-4">
          <!-- Name -->
          <div>
            <label for="serviceName" class="form-label">
              Service Name <span class="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              id="serviceName"
              v-model="serviceForm.name"
              type="text"
              class="form-input"
              :placeholder="serviceNamePlaceholder">
          </div>

          <!-- Description -->
          <div>
            <label for="serviceDescription" class="form-label">Description</label>
            <textarea
              id="serviceDescription"
              v-model="serviceForm.description"
              rows="3"
              class="form-textarea"
              placeholder="Describe what this service includes..." />
          </div>

          <!-- Pricing -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="servicePrice" class="form-label">Price</label>
              <input
                id="servicePrice"
                v-model="serviceForm.price"
                type="number"
                step="0.01"
                class="form-input"
                placeholder="0.00">
            </div>
            <div>
              <label for="servicePriceUnit" class="form-label">Price Unit</label>
              <div class="form-select-wrapper">
                <select
                  id="servicePriceUnit"
                  v-model="serviceForm.price_unit"
                  class="form-select">
                  <option value="">
                    One-time
                  </option>
                  <option value="hour">
                    Per Hour
                  </option>
                  <option value="job">
                    Per Job
                  </option>
                  <option value="sqft">
                    Per Sq Ft
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Show price toggle -->
          <div class="flex items-center gap-2">
            <input
              id="priceVisible"
              v-model="serviceForm.price_visible"
              type="checkbox"
              class="h-4 w-4 rounded border-gray-300 dark:border-gray-700 text-primary-600 focus:ring-primary-500">
            <label for="priceVisible" class="text-sm text-gray-700 dark:text-gray-200">
              Show price on website
            </label>
          </div>

          <!-- Photos -->
          <div>
            <label class="form-label">Photos</label>
            <div class="mt-2 flex flex-wrap gap-2">
              <div
                v-for="photo in serviceForm.photo_urls"
                :key="photo.id"
                class="relative h-20 w-20 overflow-hidden rounded-lg">
                <img :src="photo.url" class="h-full w-full object-cover">
                <button
                  type="button"
                  class="absolute right-1 top-1 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                  @click="handleRemoveServicePhoto(photo.id)">
                  <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Add photo button -->
              <button
                type="button"
                class="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600"
                @click="$refs.photoInput.click()">
                <svg class="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
              <input
                ref="photoInput"
                type="file"
                accept="image/*"
                multiple
                class="hidden"
                @change="handlePhotoUpload">
            </div>
          </div>
        </div>

        <!-- Modal actions -->
        <div class="mt-6 flex justify-end gap-3">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeServiceModal">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!serviceForm.name || savingService"
            @click="saveService">
            {{ savingService ? 'Saving...' : (editingService ? 'Update' : 'Add') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, reactive } from 'vue'

const props = defineProps({
  wizard: { type: Object, required: true },
  mapKey: { type: String, required: true }
})

const SERVICE_NAME_EXAMPLES = {
  medspa: 'e.g., Botox & Neurotoxins',
  spa: 'e.g., Signature Facial',
  salon: 'e.g., Balayage Color',
  dental: 'e.g., Teeth Whitening',
  optometry: 'e.g., Comprehensive Eye Exam',
  chiropractic: 'e.g., Spinal Adjustment',
  fitness: 'e.g., Personal Training Session',
  healthcare: 'e.g., Annual Physical',
  legal: 'e.g., Will & Estate Planning',
  real_estate: 'e.g., Buyer Representation',
  finance: 'e.g., Tax Preparation',
  consulting: 'e.g., Strategy Session',
  hvac: 'e.g., HVAC Repair',
  plumbing: 'e.g., Drain Cleaning',
  electrical: 'e.g., Panel Upgrade',
  restaurant: 'e.g., Private Dining',
  photography: 'e.g., Engagement Session'
}

const serviceNamePlaceholder = computed(() => {
  const industry = props.wizard?.website?.industry
  return SERVICE_NAME_EXAMPLES[industry] || 'e.g., Initial Consultation'
})

const showServiceModal = ref(false)
const editingService = ref(null)
const savingService = ref(false)

const serviceForm = reactive({
  name: '',
  description: '',
  price: null,
  price_unit: '',
  price_visible: true,
  photo_urls: []
})

function resetServiceForm() {
  serviceForm.name = ''
  serviceForm.description = ''
  serviceForm.price = null
  serviceForm.price_unit = ''
  serviceForm.price_visible = true
  serviceForm.photo_urls = []
}

function openServiceModal(service = null) {
  if (service) {
    editingService.value = service
    serviceForm.name = service.name
    serviceForm.description = service.description || ''
    serviceForm.price = service.price
    serviceForm.price_unit = service.price_unit || ''
    serviceForm.price_visible = service.price_visible
    serviceForm.photo_urls = service.photo_urls || []
  } else {
    editingService.value = null
    resetServiceForm()
  }
  showServiceModal.value = true
}

function editService(service) {
  openServiceModal(service)
}

function closeServiceModal() {
  showServiceModal.value = false
  editingService.value = null
  resetServiceForm()
}

async function saveService() {
  if (!serviceForm.name) return

  savingService.value = true

  try {
    const data = {
      name: serviceForm.name,
      description: serviceForm.description,
      price: serviceForm.price,
      price_unit: serviceForm.price_unit,
      price_visible: serviceForm.price_visible
    }

    if (editingService.value) {
      await props.wizard.updateService(editingService.value.id, data)
    } else {
      await props.wizard.addService(data)
    }

    closeServiceModal()
  } catch (err) {
    console.error('Failed to save service:', err)
    alert('Failed to save service. Please try again.')
  } finally {
    savingService.value = false
  }
}

async function handleDeleteService(serviceId) {
  if (!confirm('Are you sure you want to delete this service?')) return

  try {
    await props.wizard.removeService(serviceId)
  } catch (err) {
    console.error('Failed to delete service:', err)
    alert('Failed to delete service. Please try again.')
  }
}

async function handlePhotoUpload(event) {
  const files = Array.from(event.target.files || [])
  if (!files.length || !editingService.value) return

  try {
    await props.wizard.uploadServicePhotos(editingService.value.id, files)
    // Update local form with new photos
    const updatedService = props.wizard.services.find((s) => s.id === editingService.value.id)
    if (updatedService) {
      serviceForm.photo_urls = updatedService.photo_urls || []
    }
  } catch (err) {
    console.error('Failed to upload photos:', err)
    alert('Failed to upload photos. Please try again.')
  } finally {
    event.target.value = ''
  }
}

async function handleRemoveServicePhoto(photoId) {
  if (!editingService.value) return

  try {
    await props.wizard.removeServicePhoto(editingService.value.id, photoId)
    serviceForm.photo_urls = serviceForm.photo_urls.filter((p) => p.id !== photoId)
  } catch (err) {
    console.error('Failed to remove photo:', err)
  }
}
</script>
